<template>
  <div>
    <PopOverMenu
      class="menu-dropdown"
      placement="left"
    >
      <template #target>
        <IconButton
          size="md"
          flair="soft"
          kind="transparent"
        >
          <Icon name="mono-dots-vertical" />
        </IconButton>
      </template>

      <template #menu>
        <CardSection>
          <ListItemWithState
            flair="soft"
            class="menu-dropdown--item"
            @click.stop.prevent="deleteAPIKeyDialogOpen = true"
          >
            <template #prepend>
              <Icon
                class="menu-dropdown--item-delete--icon"
                name="mono-trash"
              />
            </template>
            <template #default>
              <span class="menu-dropdown--item-delete--label">Revoke this API Key</span>
            </template>
          </ListItemWithState>
        </CardSection>
      </template>
    </PopOverMenu>

    <ConfirmDialog
      v-model:open="deleteAPIKeyDialogOpen"
      class-name="delete-api-key-dialog"
      type="error"
      @confirm="deleteKey"
    >
      <template #title> Are you sure you wish to delete this key? </template>
      <template #detail> This action cannot be undone. </template>
    </ConfirmDialog>
  </div>
</template>

<script lang="ts">
export default {
  name: 'KeyListActions',
}
</script>

<script lang="ts" setup>
import { ref } from 'vue'

import { useToast } from '@/uiKit/Toast/useToast'
import { deleteApiKey } from '@/backend/darwin/deleteApiKey'

import IconButton from '@/uiKit/Button/IconButton.vue'
import Icon from '@/uiKit/Icon/Icon.vue'
import ListItemWithState from '@/uiKit/List/ListItemWithState.vue'

import ConfirmDialog from '@/uiKit/Dialog/ConfirmDialog.vue'

import type { ApiKeyPayload } from './types'
import { useApiKeyStore } from './useApiKeyStore'
import PopOverMenu from '@/uiKit/PopOverMenu.vue'
import CardSection from '@/uiKit/Card/CardSection.vue'

const props = defineProps<{ apiKey: ApiKeyPayload }>()

const deleteAPIKeyDialogOpen = ref(false)

const toast = useToast()

const keyStore = useApiKeyStore()

const deleteKey = async (): Promise<void> => {
  const result = await deleteApiKey(props.apiKey.id)

  if (result.ok) {
    keyStore.removeKey(props.apiKey.id)
    toast.success({ meta: { title: 'API Key has been revoked' } })
    deleteAPIKeyDialogOpen.value = false
  } else {
    toast.warning({ meta: { title: result.error.message as string } })
  }
}
</script>

<style lang="scss" scoped>
.menu-dropdown {
  &--item {
    min-width: 170px;

    &-delete {
      &--icon,
      &--label {
        color: var(--color-text-critical);
      }
    }
  }
}
</style>

<!-- eslint-disable-next-line vue-scoped-css/enforce-style-type -->
<style lang="scss">
/*  This `DarwinDialog` modal is normally called from `<modal>` and it needs to be on top of it.
    There is no current support to control z-index, so the below line is a workaround.
    `.delete-api-key-dialog` is added to the dialog so we make sure the leaked css isn't
    changing the `z-index` of other dialogs.
*/
.darwin-dialog.delete-api-key-dialog {
  z-index: var(--z-index-modal-level-2-overlay) !important;
}
</style>
